import { useEffect, useState } from 'react';

/**
 * Custom hook for managing local storage operations
 * @param {string|null} key - The key to store the value under (null to disable storage)
 * @param {any} initialValue - The initial value to use if no value exists in storage
 * @returns {[any, (value: any) => void, () => void]} - Returns the stored value, setter function, and remove function
 */
export const useLocalStorage = (key, initialValue) => {
  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = () => {
    // Prevent build error "window is undefined" but keep working
    // Also return initial value if key is null/undefined
    if (typeof window === 'undefined' || !key) {
      return initialValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  };

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(readValue);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = value => {
    // Skip localStorage operations if key is null
    if (!key) {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      return;
    }

    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore = value instanceof Function ? value(storedValue) : value;

      // Save state
      setStoredValue(valueToStore);

      // Save to local storage
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  // Remove value from localStorage
  const removeValue = () => {
    // Skip if key is null
    if (!key) {
      setStoredValue(initialValue);
      return;
    }

    try {
      // Remove from state
      setStoredValue(initialValue);

      // Remove from local storage
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(key);
      }
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  };

  useEffect(() => {
    if (key) {
      setStoredValue(readValue());
    } else {
      setStoredValue(initialValue);
    }
  }, [key]);

  return [storedValue, setValue, removeValue];
};
